import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Family, FamilyMember } from '@/types'

interface FamilyState {
  families: Family[]
  selectedFamilyId: string | null
  selectedMemberId: string | null
  // Cached members for the selected family (set by useFamilyMembers hook)
  members: FamilyMember[]
  setFamilies: (families: Family[]) => void
  setSelectedFamilyId: (id: string | null) => void
  setSelectedMemberId: (id: string | null) => void
  setMembers: (members: FamilyMember[]) => void
  getSelectedFamily: () => Family | undefined
  getSelectedMember: () => FamilyMember | undefined
  getParentMembers: () => FamilyMember[]
}

export const useFamilyStore = create<FamilyState>()(
  persist(
    (set, get) => ({
      families: [],
      selectedFamilyId: null,
      selectedMemberId: null,
      members: [],
      setFamilies: (families) => {
        // Ensure families is always an array
        const safeFamilies = Array.isArray(families) ? families : []
        set({ families: safeFamilies })
        // Auto-select first family if none selected
        const state = get()
        if (!state.selectedFamilyId && safeFamilies.length > 0) {
          set({ selectedFamilyId: safeFamilies[0].id })
        }
        // Clear selected member if family changes
        if (state.selectedFamilyId && !safeFamilies.find((f) => f.id === state.selectedFamilyId)) {
          set({ selectedMemberId: null, members: [] })
        }
      },
      setSelectedFamilyId: (selectedFamilyId) => {
        const state = get()
        // Clear selected member when family changes
        if (selectedFamilyId !== state.selectedFamilyId) {
          set({ selectedFamilyId, selectedMemberId: null, members: [] })
        } else {
          set({ selectedFamilyId })
        }
      },
      setSelectedMemberId: (selectedMemberId) => set({ selectedMemberId }),
      setMembers: (members) => {
        const safeMembers = Array.isArray(members) ? members : []
        set({ members: safeMembers })
        // Auto-select first parent if none selected and user is a caregiver
        const state = get()
        const selectedFamily = state.getSelectedFamily()
        const isCaregiver =
          selectedFamily?.role === 'PRIMARY_CAREGIVER' || selectedFamily?.role === 'CAREGIVER'
        if (isCaregiver && !state.selectedMemberId) {
          const firstParent = safeMembers.find((m) => m.role === 'PARENT')
          if (firstParent) {
            set({ selectedMemberId: firstParent.id })
          }
        }
      },
      getSelectedFamily: () => {
        const state = get()
        // Defensive check in case families is corrupted
        const families = Array.isArray(state.families) ? state.families : []
        return families.find((f) => f.id === state.selectedFamilyId)
      },
      getSelectedMember: () => {
        const state = get()
        const members = Array.isArray(state.members) ? state.members : []
        return members.find((m) => m.id === state.selectedMemberId)
      },
      getParentMembers: () => {
        const state = get()
        const members = Array.isArray(state.members) ? state.members : []
        return members.filter((m) => m.role === 'PARENT')
      },
    }),
    {
      name: 'famsteady-family',
      partialize: (state) => ({
        selectedFamilyId: state.selectedFamilyId,
        selectedMemberId: state.selectedMemberId,
      }),
    }
  )
)
